import { findOperatorMatches, MatchResult, RuleRow, shouldTransposeRow } from "./parseRules";

// AB#3431. Writing the grid back to a rule string has to produce something that
// parses into the same grid. A value the user types can hold a word the parser
// reads as an operator -- "Epic Games Twinmotion For Revit" -- and that one row
// comes back as two, with the rule quietly meaning something else.
//
// Parentheses take the spaces off the word, which is what stops both this parser
// and AidenBot from reading it as an operator, and is the workaround customer
// success already applies by hand.
//
// Rules already in a policy came out of the parser, so none of them can hold an
// operator in a value: measured over the 9247 applicability rules in the 46
// customer files, zero change shape on a re-parse. This only ever fires on what
// somebody just typed.

interface ValueSpan {
  row: number;
  start: number;
  end: number;
}

// Mirrors how ApplicabilityRuleBuilder serializes the grid, and records where each
// row's value landed so a match can be traced back to the row that owns it.
function buildWithValueSpans(rows: RuleRow[]): { text: string; spans: ValueSpan[] } {
  let text = "";
  const spans: ValueSpan[] = [];

  const append = (part: string) => {
    if (!part) return -1;
    if (text) text += " ";
    const start = text.length;
    text += part;
    return start;
  };

  rows.forEach((row, index) => {
    append(row.alias);
    const parts = shouldTransposeRow(row)
      ? ([["value", row.value], ["operator", row.operator], ["property", row.property]] as const)
      : ([["property", row.property], ["operator", row.operator], ["value", row.value]] as const);

    for (const [kind, part] of parts) {
      const start = append(part);
      if (kind === "value" && start >= 0) {
        spans.push({ row: index, start, end: start + part.length });
      }
    }
  });

  return { text, spans };
}

const wrapAt = (value: string, offset: number, length: number) =>
  `${value.slice(0, offset)}(${value.slice(offset, offset + length)})${value.slice(offset + length)}`;

export const escapeValueOperators = (rows: RuleRow[]): RuleRow[] => {
  let current = rows;

  // Every pass wraps one stretch of a value and a wrapped stretch can never match
  // again, so one pass per word is more than the work can ever need.
  const maxPasses = rows.reduce((total, row) => total + (row.value ? row.value.split(/\s+/).length : 0), 1);

  for (let pass = 0; pass < maxPasses; pass++) {
    const { text, spans } = buildWithValueSpans(current);

    // An operator can start on the row's own alias and reach into the value --
    // "when" followed by a value opening with "a" is read back as "when a" -- so
    // overlapping a value is what makes a match an intruder, not sitting inside it.
    let intruder: MatchResult | undefined;
    let owner: ValueSpan | undefined;
    for (const match of findOperatorMatches(text)) {
      const matchEnd = match.startIndex + match.length;
      const span = spans.find((s) => match.startIndex < s.end && matchEnd > s.start);
      if (span) {
        intruder = match;
        owner = span;
        break;
      }
    }

    if (!intruder || !owner) return current;

    // Wrap only the part that falls inside the value, so the row keeps its alias.
    const from = Math.max(intruder.startIndex, owner.start) - owner.start;
    const to = Math.min(intruder.startIndex + intruder.length, owner.end) - owner.start;
    const target = owner.row;
    current = current.map((row, index) => (index === target ? { ...row, value: wrapAt(row.value, from, to - from) } : row));
  }

  return current;
};
